import {
  Component,
  ComponentRef, ElementRef, Input, OnDestroy,
  OnInit, QueryList, Renderer2,
  ViewChild, ViewChildren, ViewContainerRef
} from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { CdkDragDrop, CdkDragEnter, CdkDragExit, CdkDragMove, CdkDragStart } from '@angular/cdk/drag-drop';
import { forkJoin, Subscription, concat } from 'rxjs';
import { NotifierService } from 'angular-notifier';
import { AwallComponentEvent } from '../../models/awallComponentEvent';
import { ComponentEvent } from '../../models/enums/componentEvent';
import { SettingsEventType } from '../../models/enums/settingsEventType';
import { Issue } from '../../models/issue';
import { Status } from '../../models/status';
import { Statuscategory } from '../../models/statuscategory';
import { Userstory } from '../../models/userstory';
import { SettingsService } from '../../services/settings.service';
import { SprintPlanning2Service } from '../../services/sprint-planning2.service';
import { SynchronizationService } from '../../services/synchronization.service';
import { UserstoryService } from '../../services/userstory.service';
import { HTMLElementPositioningHelper } from '../../util/htmlelement-positioning-helper';
import { IssueComponent } from '../issue/issue.component';
import { ProductBacklogSynchroHelper } from './product-backlog-synchro-helper';
import { moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { CommunicationService } from '../../services/communication.service';


@Component({
  selector: 'app-product-backlog',
  templateUrl: './product-backlog.component.html',
  styleUrls: ['./product-backlog.component.scss'],
  host: { 'style': 'display: contents' },
  animations: [
  trigger('productBacklog', [
    state('closed', style({ width: '44px' })),
    state('open', style({ width: '18vw' })),
    transition('closed => open', [animate('0.25s')]),
    transition('open => closed', [animate('0.25s')])
    ])
  ]
  })
export class ProductBacklogComponent implements OnInit, OnDestroy {
  @ViewChild('newBacklogIssueComponent', { read: ViewContainerRef }) newBacklogIssueComponentChild: ViewContainerRef;
  @ViewChildren(IssueComponent) issueComponentChildren: QueryList<IssueComponent>;
  @ViewChildren(IssueComponent, { read: ElementRef }) issueComponentChildrenElementRef: QueryList<ElementRef>;
  @ViewChild('backlogScrollContainer') scrollContainer: ElementRef;
  @Input() private statuses: Status[];
  public productBacklogIsOpen: boolean = (/true/i).test(localStorage.getItem('productBacklogStatus'));
  public projectKey: string;
  public issues: Userstory[] = [];
  public subscriptions: Subscription[] = [];
  public static readonly componentId = 'ProductBacklogComponent';
  private readonly sprintId;
  private readonly BACKLOG_CONTAINER_ID = 'product-backlog-issue-container';


  constructor(
    private userstoryService: UserstoryService,
    private settingsService: SettingsService,
    private sprintPlanning2Service: SprintPlanning2Service,
    private userStoryService: UserstoryService,
    private communicationService: CommunicationService,
    private notifier: NotifierService,
    private renderer: Renderer2,
    private syncHelper: ProductBacklogSynchroHelper,
    private sync: SynchronizationService,
    private positionHelper: HTMLElementPositioningHelper
  ) {
    this.projectKey = settingsService.getProjectName();
    this.sprintId = settingsService.getSprintId(settingsService.getProjectName());
  }

  ngOnInit(): void {
    this.syncHelper.initSyncEventListeners(this);
    this.communicationService.registerComponent(ProductBacklogComponent.componentId, this);
    this.subscriptions.push(this.settingsService.getUpdateObservable().subscribe(settingsEventType => {
      if (settingsEventType === SettingsEventType.PROJECT_CHANGED) {
        this.projectKey = this.settingsService.getProjectName();
        this.updateViewData();
      }
    }));
  }

  ngOnDestroy(): void {
    this.communicationService.unregisterComponent(ProductBacklogComponent.componentId);
    for (const ev of this.subscriptions) {
      if (ev) {
        ev.unsubscribe();
      }
    }
  }

  onCommunicationEvent(event: AwallComponentEvent): void {
    if (!event) { return; }

    switch (event.name) {
      case ComponentEvent.BACKLOG_REQUEST_UPDATE: {
        this.updateViewData();
        break;
      }
    }
  }

  updateViewData(): void {
    this.clearViewData();
    const projectKey = this.settingsService.getProjectName();

    // We can only load new data if we have a project to load
    if (!projectKey) {
      return;
    }

    this.notifier.notify('info', 'Loading Backlog...');
    forkJoin([
      this.userstoryService.getUserstoriesFromBacklog(projectKey, 'story, task, bug, epic')
    ]).subscribe(([userstories]) => {
      // We clear the current data again here. There might have been another async call filling
      // in data while this async has been waiting and we would get duplicat data.
      this.clearViewData();
      this.issues = userstories;
    });
  }

  clearViewData(): void {
    this.issues = [];
  }

  public toggleProductBacklog(): void {
    this.productBacklogIsOpen = !this.productBacklogIsOpen;
    localStorage.setItem('productBacklogStatus', String(this.productBacklogIsOpen));
    this.sync.sendRealTimeEvent('toggleProductBacklog', { 'productBacklogIsOpen': this.productBacklogIsOpen });
  }

  /**
   * Open a dialog (IssueCard) for the creation of a new Story in Product Backlog
   */
  openNewIssueDialog(event: MouseEvent): void {
    // Create new Task
    const newIssueId: number = this.userstoryService.getTemporaryIdForNewIssue();
    const issueComp: IssueComponent = this.createIssueComponent(newIssueId);
    // Show the IssueCard modal window
    issueComp.createIssuecardForIssue(event, issueComp.issue, false);
  }

  /**
   * Create a new issueComponent for task creation inside story
   * @param issueId parent story ID
   */
  createIssueComponent(issueId: number): IssueComponent {
    // Create new Task
    const statusTodo: Status = new Status('', '', 10000, 'To Do', new Statuscategory('blue-gray', 2, 'new'));
    const newIssue: Userstory = new Userstory(null, '', undefined, issueId, null,
      null, '', 1, null, statusTodo, [], [], null, null, null, false, null, null, '', []);

    // Dynamically create a new Issue in the row
    const componentRef: ComponentRef<IssueComponent> = this.newBacklogIssueComponentChild.createComponent(IssueComponent);
    const issue: IssueComponent = componentRef.instance;
    issue.issue = newIssue;
    issue.projectKey = this.settingsService.getProjectName();
    issue.componentRef = componentRef;

    componentRef.instance.issueCreated.subscribe(createdIssue => {
      this.onNewIssueCreated(createdIssue);
    });
    return issue;
  }

  getConnectedIssues(): any[] {
    return this.sprintPlanning2Service.getConnectedIssues();
  }

  onIssueDrop(event: CdkDragDrop<Userstory[]>): void {
    const sourceContainer = event.previousContainer;
    const targetContainer = event.container;
    const projectKey = this.settingsService.getProjectName();

    // Issue of another container dropped into backlog. Persist the change
    if (targetContainer.id === this.BACKLOG_CONTAINER_ID && sourceContainer.id !== this.BACKLOG_CONTAINER_ID) {
      const issue: Userstory = sourceContainer.data[event.previousIndex];
      transferArrayItem(sourceContainer.data, targetContainer.data, event.previousIndex, event.currentIndex);
      concat(
        this.userstoryService.addUserStoriesToProductBacklog(String(projectKey), [issue]),
        this.userstoryService.changeRank(projectKey, issue, targetContainer.data)
      ).subscribe();

      // TODO 'eventSource'-String needs to be removed and and we need another way to inform the source component of the dropped issue
      this.sprintPlanning2Service.sendSyncEventIssueMoveEnded(event.item.data.id, 'sp2');
      this.sprintPlanning2Service.sendSyncEventMoveIssueToProductBacklog(targetContainer.data[event.currentIndex],
        event.previousIndex, event.currentIndex);
    }

    // Issue of the backlog dropped into this sprint. Just change the issue order inside this sprint
    if (sourceContainer.id === targetContainer.id) {
      const issue: Userstory = sourceContainer.data[event.previousIndex];
      moveItemInArray(targetContainer.data, event.previousIndex, event.currentIndex);
      this.userstoryService.changeRank(projectKey, issue, targetContainer.data).subscribe();
      this.sprintPlanning2Service.sendSyncEventIssueMoveEnded(event.item.data.id, 'backlog');
      this.sprintPlanning2Service.sendSyncEventChangeIssueOrder('product-backlog', event.previousIndex, event.currentIndex);
    }
  }

  // TODO refactor to remove dependency from sp2-board/sp2Servie
  onIssueDragListEnter(event: CdkDragEnter<any>): void {
    const styleParameter: any[] = this.sprintPlanning2Service.onIssueDragListEnter(event);
    // doesnt work on boards that are not sp2 yet, first parameter will be null
    if (styleParameter[0]) {
      // @ts-ignore
      this.renderer.setStyle(...styleParameter);
    }
  }

  // TODO refactor to remove dependency from sp2-board/sp2Servie
  onIssueDragListEnterExit(event: CdkDragExit<any>): void {
    const styleParameter: any[] = this.sprintPlanning2Service.onIssueDragListEnterExit(event);
    // doesnt work on boards that are not sp2 yet, first parameter will be null
    if (styleParameter[0]) {
      // @ts-ignore
      this.renderer.setStyle(...styleParameter);
    }
  }

  onNewIssueCreated(issue: Issue): void {
    if (issue instanceof Userstory) {
      // Add new issue at the top of the backlog and also set it rank accordingly
      this.rankIssueOnTop(issue);
      this.issues.splice(0, 0, issue);
    }
  }


  /**
   * Sets the rank for issue to be above the first issue loaded in this backlog.
   */
  private rankIssueOnTop(issue: Issue): void {
    if (this.issues.length < 2) { return; }
    this.userStoryService.changeRankOfUserstories(this.projectKey, new Array(<Userstory>issue), this.issues[0], true).subscribe();
  }

  onIssueRemoved(id: number): void {
    this.issues.forEach((issue, index) => {
      if (Number(issue.id) === Number(id)) {
        this.issues.splice(index, 1);
      }
    });
  }

  onIssueMoveStarted(event: CdkDragStart<Userstory>) {
    this.sync.sendRealTimeEvent('IssueMoveStarted', { 'id': event.source.data.id, 'source': 'backlog' });
  }

  onIssueMoved(event: CdkDragMove<Userstory>) {
    const position = this.positionHelper.getRelativePosition(event.pointerPosition.x, event.pointerPosition.y);
    const data = {
      'id': event.source.data.id,
      'position': position,
      'source': 'backlog'
    };
    this.sync.sendRealTimeEvent('IssueMoved', data);
  }

  getIssueComponent(issueId: number): IssueComponent {
    return this.issueComponentChildren.find(child => Number(child.issue.id) === Number(issueId));
  }

  getNativeIssueComponent(issueId: number): ElementRef {
    return this.issueComponentChildrenElementRef.find(child => Number(child.nativeElement.dataset.taskcardid) === Number(issueId));
  }

  markAllAsRead(): void {
    const projectKey = this.settingsService.getProjectName();
    this.userstoryService.markAllAsRead(projectKey).subscribe();
  }
}
